import { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PackageCubeIcon from "./icons/PackageCubeIcon";
import { formatRelativeTime } from "./notificationDisplay";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import "./notificationsDrawer.css";

// The meta line ("via User App · pre 2 sata") is still keyed by WHO triggered the action
// (n.source, derived in notificationDisplay.js from the API's `created_by_type`) — a member's
// own action from the User App vs. a coach/admin's from the Office App, both shown side by
// side in this one drawer.
const SOURCE_LABEL = {
  "user-app": "via User App",
  "office-app": "via Office App",
};

// The row icon/left-accent/unread-tint, on the other hand, is keyed by WHAT happened
// (n.category, also from notificationDisplay.js) — a package request needs to visually jump
// out from a booking regardless of who created either, which `source` alone can't express
// (a member can trigger both a booking AND a package request). Package uses a dedicated
// purple token (--hf-pkg-accent, theme.css) rather than the app's existing indigo
// --hf-office-app swatch — a deliberately distinct hue from booking's blue (--hf-user-app),
// per the reference design.
const TYPE_VARIANT = {
  package: { icon: PackageCubeIcon, className: "cat-package" },
  booking: { icon: EventOutlinedIcon, className: "cat-booking" },
  other: { icon: NotificationsNoneOutlinedIcon, className: "cat-other" },
};

// Single-select, not a multi-toggle Set anymore — "Sve" | "package" | "booking", exactly one
// active at a time (was a Set where individually selecting every type also silently turned
// "Sve" on, and unclear whether clicking a type added it to or replaced the current selection).
// "Sve" still means every category, Ostalo notifications included, even though it has no pill
// of its own here — there was never a request to lose the ability to see them, just to stop
// giving that one category its own filter chip (per the reference design).
const VISIBLE_TYPE_PILLS = ["package", "booking"];
const TYPE_FILTER_LABEL = { package: "Paketi", booking: "Zakazivanja" };

// Right-side slide-in drawer shared visually by both apps (same overlay/panel/animation
// convention as web_user_v2's NotificationsDrawer). Always mounted (never conditionally
// rendered) so the transform/opacity transition actually plays on open AND close — this app
// otherwise has no precedent for animated overlays, all existing modals just mount instantly.
const NotificationsDrawer = ({
  open,
  onClose,
  notifications,
  onMarkAllRead,
  onNotificationClick,
}) => {
  const touchStartX = useRef(null);
  const unreadCount = notifications.filter((n) => !n.read).length;
  // Always mounted (per the animation comment above), so the lock is gated on `open`.
  useLockBodyScroll(open);

  // Default landing state per spec: only unread, every type — reset each time the drawer is
  // (re)opened rather than persisted, so a trainer never has to remember to switch back after
  // browsing "Sve" the last time they had this open.
  const [statusFilter, setStatusFilter] = useState("unread");
  const [typeFilter, setTypeFilter] = useState("all");
  useEffect(() => {
    if (open) {
      setStatusFilter("unread");
      setTypeFilter("all");
    }
  }, [open]);

  const visibleNotifications = useMemo(
    () =>
      notifications.filter(
        (n) =>
          (statusFilter === "all" || !n.read) &&
          (typeFilter === "all" || n.category === typeFilter)
      ),
    [notifications, statusFilter, typeFilter]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx > 60) onClose();
  };

  return (
    <div
      className={`hf-notif-overlay${open ? " open" : ""}`}
      aria-hidden={!open}
    >
      <div
        className={`hf-notif-panel${open ? " open" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        role="dialog"
        aria-label="Obaveštenja"
      >
        <div className="hf-notif-header">
          <h2>Obaveštenja</h2>
          <div className="hf-notif-header-actions">
            {unreadCount > 0 && (
              <button type="button" className="hf-notif-markall" onClick={onMarkAllRead}>
                Označi sve
              </button>
            )}
            <button
              type="button"
              className="hf-notif-close"
              aria-label="Zatvori"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div className="hf-notif-filters">
          <div className="hf-notif-type-row">
            <button
              type="button"
              className={`hf-notif-type-pill${typeFilter === "all" ? " active" : ""}`}
              onClick={() => setTypeFilter("all")}
            >
              Sve
            </button>
            {VISIBLE_TYPE_PILLS.map((category) => (
              <button
                key={category}
                type="button"
                className={`hf-notif-type-pill${typeFilter === category ? " active" : ""}`}
                onClick={() => setTypeFilter(category)}
              >
                {TYPE_FILTER_LABEL[category]}
              </button>
            ))}
          </div>

          {/* Replaces the old Nepročitane/Sve tab toggle — unchecked (default) is still
              unread-only, checked reveals already-read notifications too. */}
          <label className="hf-notif-status-check">
            <input
              type="checkbox"
              checked={statusFilter === "all"}
              onChange={(e) => setStatusFilter(e.target.checked ? "all" : "unread")}
            />
            Sva obaveštenja
          </label>
        </div>

        <div className="hf-notif-list">
          {notifications.length === 0 ? (
            <div className="hf-notif-empty">
              <div className="hf-notif-empty-icon">
                <NotificationsNoneOutlinedIcon />
              </div>
              <p className="hf-notif-empty-title">Nema obaveštenja</p>
              <p className="hf-notif-empty-sub">Sve je pročitano.</p>
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="hf-notif-empty">
              <div className="hf-notif-empty-icon">
                <NotificationsNoneOutlinedIcon />
              </div>
              <p className="hf-notif-empty-title">Nema notifikacija za ovaj filter</p>
              <p className="hf-notif-empty-sub">Promeni status ili tip da vidiš više.</p>
            </div>
          ) : (
            visibleNotifications.map((n) => {
              const variant = TYPE_VARIANT[n.category] || TYPE_VARIANT.other;
              const TypeIcon = variant.icon;
              return (
                <button
                  key={n.id}
                  type="button"
                  className={`hf-notif-row ${variant.className}${n.read ? "" : " unread"}`}
                  onClick={() => onNotificationClick(n)}
                >
                  <span className={`hf-notif-row-icon ${variant.className}`}>
                    <TypeIcon fontSize="small" />
                  </span>
                  <span className="hf-notif-row-body">
                    <span className="hf-notif-row-category">{n.categoryLabel}</span>
                    <span className="hf-notif-row-message">{n.message}</span>
                    <span className="hf-notif-row-meta">
                      <span className="hf-notif-row-source">
                        {SOURCE_LABEL[n.source] || SOURCE_LABEL["office-app"]}
                      </span>
                      <span aria-hidden="true">·</span>
                      <span className="hf-notif-row-time">{formatRelativeTime(n.timestamp)}</span>
                    </span>
                  </span>
                  {!n.read && <span className="hf-notif-row-dot" aria-hidden="true" />}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsDrawer;
